import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Button,
  Card,
  DataList,
  DataRow,
  linkTextClasses,
  NotProvided,
  Page,
  PageHeader,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import { canAccess, formatDate, loadFailureMessage } from '@evogard/domain';
import { MoneyValue } from '../../components/Values.js';
import { backSearch } from '../../lib/list-search.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { RefreshOutcome, useErpRefresh } from './common.js';
import { plainText } from '../../lib/plain-text.js';
import { VocabularyLabel } from './history.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * UM atendimento do SGA — a página que o botão "Ver" da lista abre
 * (14/10/2026).
 *
 * A lista mostra três linhas da descrição; aqui ela vem inteira, como texto
 * (o SGA a guarda em HTML — ver `plainText`). O endereço é o
 * `codigo_atendimento` do SGA: o atendimento não tem código da casa, e o do SGA
 * não é renumerado, porque o núcleo o guarda como procedência.
 */
export function ServiceRecordScreen() {
  const { code } = useParams({ from: '/authenticated/erp/atendimentos/$code' });
  const { data: context } = useSessionContext();
  const canRefresh = canAccess(context?.permissions ?? [], 'erp.sync', 'executar');

  /*
   * O SGA não busca UM atendimento: ele entrega o histórico do associado
   * inteiro. Então o "Atualizar do ERP" daqui atualiza o associado — e com ele
   * este atendimento.
   */
  const queryKey = ['erp', 'atendimento', code] as const;
  const { mutation: refresh, follow } = useErpRefresh(queryKey, () =>
    a?.memberErpCode ? { entity: 'associado', id: a.memberErpCode } : null,
  );

  const query = useQuery({
    refetchInterval: follow,
    queryKey,
    queryFn: async () => {
      const r = await api.erp.serviceRecord({ params: { code } });
      if (r.status === 404)
        throw new NotFoundError('Este atendimento não existe no cadastro desta base');
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o atendimento'));
      return r.body;
    },
    retry: false,
  });
  const a = query.data;

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/erp/atendimentos', label: 'Ver os atendimentos' }}
      />
    );
  }

  return (
    <Page measure="leitura">
      <Link
        to="/erp/atendimentos"
        search={backSearch('/erp/atendimentos') as never}
        className={backLinkClasses()}
      >
        Atendimentos
      </Link>

      <PageHeader title={a?.title ?? (query.isLoading ? 'Carregando…' : 'Atendimento')}>
        {a && `Atendimento ${a.erpCode ?? code} no ERP`}
        {a?.registeredOn && ` · registrado em ${formatDate(a.registeredOn)}`}
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {a && canRefresh && a.memberErpCode && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            icon="atualizar"
          >
            {refresh.isPending ? 'Atualizando…' : 'Atualizar do ERP'}
          </Button>
          <span className="text-sm text-texto-suave">
            traz o histórico de atendimento inteiro do associado
          </span>
        </div>
      )}

      <RefreshOutcome mutation={refresh} />

      {a?.missing && (
        <Alert tone="alerta">
          Este atendimento saiu do histórico do associado no ERP. A casa guarda o que ele
          dizia.
        </Alert>
      )}

      {a && (
        <Card title="O atendimento">
          <DataList>
            <DataRow label="Associado">
              {a.member ? (
                <Link
                  to="/erp/associados/$code"
                  params={{ code: a.member.code }}
                  className={linkTextClasses()}
                >
                  {a.member.nome ?? a.member.code}
                </Link>
              ) : (
                <NotProvided />
              )}
            </DataRow>
            <DataRow label="Veículo">
              {a.vehicle ? (
                <Link
                  to="/erp/veiculos/$code"
                  params={{ code: a.vehicle.code }}
                  className={linkTextClasses()}
                >
                  {a.vehicle.placa ?? a.vehicle.code}
                </Link>
              ) : (
                <NotProvided>sem veículo</NotProvided>
              )}
            </DataRow>
            <DataRow label="Tipo">
              <VocabularyLabel label={a.typeLabel} code={a.typeCode} />
            </DataRow>
            <DataRow label="Departamento">
              <VocabularyLabel label={a.departmentLabel} code={a.departmentCode} />
            </DataRow>
            <DataRow label="Status">
              <VocabularyLabel label={a.statusLabel} code={a.statusCode} badge />
            </DataRow>
            <DataRow label="Registrado em">
              {a.registeredOn ? formatDate(a.registeredOn) : <NotProvided />}
            </DataRow>
            <DataRow label="Atendido em">
              {a.servedOn ? formatDate(a.servedOn) : <NotProvided />}
            </DataRow>
            <DataRow label="Valor">
              {a.amount && Number(a.amount) !== 0 ? <MoneyValue value={a.amount} /> : '—'}
            </DataRow>
            <DataRow label="Quem registrou">{a.author ?? <NotProvided />}</DataRow>
          </DataList>
        </Card>
      )}

      {a && (
        <Card title="Descrição">
          {a.description ? (
            <p className="whitespace-pre-line text-sm leading-relaxed">
              {plainText(a.description)}
            </p>
          ) : (
            <p className="text-sm text-texto-suave">
              O atendimento não tem descrição no ERP.
            </p>
          )}
        </Card>
      )}
    </Page>
  );
}
